/** One row of a thread as `MessageThread` lays it out. Apps map their message type onto it. */
export interface ThreadItem {
  id: string;
  /**
   * Consecutive items with the same key inside the group window share one author line. `null`
   * (system events) never groups.
   */
  authorKey: string | null;
  authorName: string;
  avatarUrl?: string | null;
  /** `start`: the other party; `end`: our side; `center`: an event row across the thread. */
  align: 'start' | 'end' | 'center';
  /** `internal`: a note only our side sees (a whisper). */
  tone?: 'default' | 'internal';
  /** ISO 8601. */
  createdAt: string;
}

export interface ThreadGroup<T extends ThreadItem> {
  key: string;
  /** Local calendar day of the first item, `YYYY-MM-DD`, for day separators. */
  day: string;
  authorKey: string | null;
  align: ThreadItem['align'];
  tone: NonNullable<ThreadItem['tone']>;
  items: T[];
}

export const DEFAULT_GROUP_WINDOW_MS = 5 * 60_000;

function dayOf(iso: string): string {
  const date = new Date(iso);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * Groups a thread (oldest first) into runs by one author, one tone and one day, each item at
 * most `windowMs` after the previous one. Event rows stand alone.
 */
export function groupThreadItems<T extends ThreadItem>(
  items: readonly T[],
  windowMs = DEFAULT_GROUP_WINDOW_MS,
): Array<ThreadGroup<T>> {
  const groups: Array<ThreadGroup<T>> = [];
  let previous: T | undefined;
  for (const item of items) {
    const tone = item.tone ?? 'default';
    const day = dayOf(item.createdAt);
    const current = groups.at(-1);
    const joins =
      current !== undefined &&
      previous !== undefined &&
      item.authorKey !== null &&
      current.authorKey === item.authorKey &&
      current.align === item.align &&
      current.tone === tone &&
      current.day === day &&
      Date.parse(item.createdAt) - Date.parse(previous.createdAt) <= windowMs;
    if (joins) {
      current.items.push(item);
    } else {
      groups.push({
        key: item.id,
        day,
        authorKey: item.authorKey,
        align: item.align,
        tone,
        items: [item],
      });
    }
    previous = item;
  }
  return groups;
}
